import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import styles from "./SearchableSelect.module.css";

const EMPTY_OPTIONS = [];
const normalizeOptions = (options) => options.map((option) => (
  typeof option === "object" ? option : { value: option, label: String(option) }
));
const uniqueOptions = (options) => [...new Map(options.map((option) => [option.value, option])).values()];

/**
 * Single selection: value/onChange use a scalar (existing callers are unchanged).
 * multiple: value/onChange use arrays of option values.
 * options: strings or { value, label } objects, searched and batched locally.
 * Optional loadOptions({ search, page, pageSize, signal }) returns
 * { options: [{ value, label }], hasMore: boolean } for server-side loading.
 * Keep loadOptions stable (module-level function or useCallback).
 */
export default function SearchableSelect({
  value,
  onChange,
  options = EMPTY_OPTIONS,
  multiple = false,
  loadOptions,
  pageSize = 30,
  debounceMs = 300,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  ariaLabel,
  style,
  className = "",
  disabled = false,
  emptyText = "No matches",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [remote, setRemote] = useState({ options: [], loading: false, hasMore: false, error: "" });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const sessionRef = useRef(null);
  const labelCache = useRef(new Map());
  const listId = useId();
  const expanded = open && !disabled;
  const label = ariaLabel || placeholder;
  const items = useMemo(() => normalizeOptions(options), [options]);
  const selected = multiple ? (value || []) : (value === "" || value == null ? [] : [value]);
  const selectedSet = new Set(selected);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return search ? items.filter((option) => option.label.toLowerCase().includes(search)) : items;
  }, [items, query]);
  const visibleOptions = loadOptions ? remote.options : filtered.slice(0, visibleCount);
  const hasMore = loadOptions ? remote.hasMore : visibleCount < filtered.length;

  // Preserve labels when selected options leave the current remote search/page.
  useEffect(() => {
    [...items, ...remote.options].forEach((option) => labelCache.current.set(option.value, option.label));
  }, [items, remote.options]);
  const optionLabel = (key) => items.find((option) => option.value === key)?.label
    ?? remote.options.find((option) => option.value === key)?.label
    ?? labelCache.current.get(key) ?? String(key);
  const selectedLabel = selected.slice(0, 2).map(optionLabel).join(", ");

  const fetchPage = useCallback(async (session, page) => {
    if (!session || session.busy || session.controller.signal.aborted) return;
    session.busy = true;
    setRemote((current) => ({ ...current, loading: true, error: "" }));
    try {
      const result = await loadOptions({ search: session.search, page, pageSize, signal: session.controller.signal });
      if (sessionRef.current !== session || session.controller.signal.aborted) return;
      session.page = page;
      setRemote((current) => ({
        options: uniqueOptions([...current.options, ...normalizeOptions(result.options)]),
        hasMore: Boolean(result.hasMore), loading: false, error: "",
      }));
    } catch (error) {
      if (sessionRef.current !== session || session.controller.signal.aborted) return;
      setRemote((current) => ({ ...current, loading: false, error: error.message || "Couldn't load options." }));
    } finally {
      session.busy = false;
    }
  }, [loadOptions, pageSize]);

  useEffect(() => {
    if (!expanded || !loadOptions) return;
    const session = { controller: new AbortController(), search: query.trim(), page: 0, busy: false };
    sessionRef.current = session;
    setRemote({ options: [], loading: true, hasMore: false, error: "" });
    const timer = setTimeout(() => fetchPage(session, 1), query ? debounceMs : 0);
    return () => {
      clearTimeout(timer);
      session.controller.abort();
    };
  }, [expanded, query, loadOptions, fetchPage, debounceMs]);

  useEffect(() => {
    setVisibleCount(pageSize);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query, items, pageSize, expanded]);

  useEffect(() => {
    if (expanded) inputRef.current?.focus();
    else setQuery("");
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    function onDocClick(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [expanded]);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function pick(key) {
    if (multiple) {
      onChange(selectedSet.has(key) ? selected.filter((item) => item !== key) : [...selected, key]);
    } else {
      onChange(key);
      close();
    }
  }

  function loadMore() {
    if (!hasMore) return;
    if (loadOptions) {
      if (!remote.loading && !remote.error) fetchPage(sessionRef.current, sessionRef.current.page + 1);
    } else {
      setVisibleCount((count) => count + pageSize);
    }
  }

  function navigateOptions(event) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    if (event.target === inputRef.current && !["ArrowDown", "ArrowUp"].includes(event.key)) return;
    const buttons = Array.from(listRef.current?.querySelectorAll('[role="option"]') || []);
    if (!buttons.length) return;
    event.preventDefault();
    const index = buttons.indexOf(document.activeElement);
    const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1
      : event.key === "ArrowDown" ? Math.min(index + 1, buttons.length - 1)
        : index < 0 ? buttons.length - 1 : Math.max(index - 1, 0);
    buttons[nextIndex].focus();
  }

  return (
    <div ref={rootRef} className={`${styles.root} ${className}`} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }} onKeyDown={(event) => {
      if (event.key === "Escape" && expanded) { event.preventDefault(); event.stopPropagation(); close(); }
    }}>
      <div className={styles.control}>
        <button ref={triggerRef} type="button" className={styles.trigger} style={style} disabled={disabled}
          aria-label={label} aria-haspopup="listbox" aria-expanded={expanded} aria-controls={expanded ? listId : undefined}
          title={selected.map(optionLabel).join(", ")} onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") { event.preventDefault(); setOpen(true); }
          }}>
          <span className={selected.length ? styles.value : styles.placeholder}>
            {selected.length ? `${selectedLabel}${selected.length > 2 ? ` +${selected.length - 2}` : ""}` : placeholder}
          </span>
          <span aria-hidden="true" className={styles.caret}>▾</span>
        </button>
        {multiple && selected.length > 0 && <button type="button" className={styles.clear} disabled={disabled}
          aria-label={`Clear ${label}`} onClick={() => onChange([])}>×</button>}
      </div>
      {expanded && (
        <div className={styles.popup} onKeyDown={navigateOptions}>
          {multiple && selected.length > 0 && <div className={styles.chips}>
            {selected.map((key) => <button key={key} type="button" className={styles.chip}
              aria-label={`Remove ${optionLabel(key)}`} onClick={() => pick(key)}>
              {optionLabel(key)} <span aria-hidden="true">×</span>
            </button>)}
          </div>}
          <div className={styles.search}>
            <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder} aria-label={`Search ${label}`} aria-controls={listId} />
          </div>
          <div ref={listRef} id={listId} role="listbox" aria-label={label}
            aria-multiselectable={multiple || undefined} aria-busy={loadOptions ? remote.loading : undefined}
            className={styles.list} onScroll={(event) => {
              const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
              if (scrollHeight - scrollTop - clientHeight < 40) loadMore();
            }}>
            {!multiple && placeholder && <button type="button" role="option" aria-selected={!selected.length}
              className={`${styles.option} ${styles.placeholder}`} onClick={() => pick("")}>{placeholder}</button>}
            {visibleOptions.map((option) => <button key={option.value} type="button" role="option"
              aria-selected={selectedSet.has(option.value)} className={styles.option} onClick={() => pick(option.value)}>
              {multiple && <span aria-hidden="true" className={styles.check}>{selectedSet.has(option.value) ? "☑" : "☐"}</span>}
              {option.label}
            </button>)}
          </div>
          <div className={styles.status} aria-live="polite">
            {loadOptions && remote.loading ? "Loading…" : loadOptions && remote.error ? (
              <><span>{remote.error}</span> <button type="button" onClick={() => fetchPage(sessionRef.current, sessionRef.current.page + 1)}>Retry</button></>
            ) : !visibleOptions.length ? emptyText : hasMore ? (
              <button type="button" onClick={loadMore}>Load more</button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
